import type { Booking, RecordPaymentInput } from '../api/bookings';
import { allocateParts, filledParts, splitTotal, type SplitPart } from '../cash/split';
import { typedNumber } from '../catalogue/forms';
import { formatAmount } from '../format/money';
import { depositSplit } from './deposit';

/**
 * Handing over and taking the money in one press — the phone's hand-over
 * (`docs/Mobile_Redesign_Plan.md`, step 5).
 *
 * <p>
 * <strong>Issuing still never takes money.</strong> The hand-over is sent first and raises its
 * invoice; then the money is recorded through the ordinary payment, `RecordPaymentAsync`, the one
 * path cash reaches the ledger by. Two calls because they are two facts, as taking a booking and its
 * token already are.
 * </p>
 *
 * <p>
 * <strong>The amounts come from the booking the hand-over answered with</strong>, never from the
 * screen's preview: once the invoice exists, the balance carries its GST, and that figure is the
 * server's. So the money recorded is exactly what the booking then says is owed — the rental (GST
 * included) and the deposit if it has not been taken — and nothing when nothing is.
 * </p>
 *
 * <p>
 * The hand-over's account boxes as payments (split payments, owner 2026-11-01): the rental balance
 * first — its GST included, read off the booking the hand-over answered with — then the deposit, out
 * of the boxes in the order shown, each payment naming its account. What the boxes do not reach is
 * left owing; nothing is taken beyond what the booking then says is owed.
 * </p>
 */
export function paymentsAfterIssueInParts(
  issued: Booking,
  parts: readonly SplitPart[],
): RecordPaymentInput[] {
  return allocateParts(
    [
      { kind: 'RentalBalance' as const, amount: Math.max(0, issued.balanceDue) },
      { kind: 'Deposit' as const, amount: depositSplit(issued.deposit).due },
    ],
    parts,
  ).map(({ kind, part, amount }) => ({
    kind,
    method: part.method,
    amount,
    reference: part.reference.trim() || null,
    cashAccountId: part.accountId,
  }));
}

/**
 * What stops the hand-over taking the money in its boxes: a box that is not a figure, or more than
 * "Take now" — the customer cannot be charged past what is owed. Less is allowed and says what is
 * left owing (`handoverLeftOwing`); nothing typed takes nothing, as Later does.
 */
export function handoverPartsProblem(
  takeNow: number,
  parts: readonly SplitPart[],
  currency = '₹',
): string | null {
  const bad = parts.find((part) => {
    const amount = typedNumber(part.amount);

    return amount !== null && (Number.isNaN(amount) || amount < 0);
  });

  if (bad) {
    return `The amount for ${bad.accountName} must be a figure above zero.`;
  }

  const total = splitTotal(parts);

  return total > takeNow + 0.004
    ? `The boxes come to ${currency}${formatAmount(total)} — more than the ${currency}${formatAmount(takeNow)} to take now.`
    : null;
}

/** "₹2,000 left owing" under the boxes when they take less than "Take now"; null when they do not. */
export function handoverLeftOwing(
  takeNow: number,
  parts: readonly SplitPart[],
  currency = '₹',
): string | null {
  if (filledParts(parts).length === 0) {
    return null;
  }

  const left = Math.round((takeNow - splitTotal(parts)) * 100) / 100;

  return left > 0
    ? `${currency}${formatAmount(left)} left owing — take it later from the booking.`
    : null;
}

/**
 * The hand-over button: what goes out, and the money when it is being taken —
 * "Hand over 2 · take ₹23,376", "Hand over 1 garment".
 */
export function handoverButton({
  garments,
  take,
  currency = '₹',
}: {
  garments: number;
  /** What is taken with it, or null when it is not (no grant, nothing owed, or "later"). */
  take: number | null;
  currency?: string;
}): string {
  if (take !== null && take > 0) {
    return `Hand over ${garments} · take ${currency}${formatAmount(take)}`;
  }

  return `Hand over ${garments} ${garments === 1 ? 'garment' : 'garments'}`;
}
